import {
  type Address,
  type Hex,
  encodeFunctionData,
  getAddress,
  parseUnits,
} from 'viem'

export const LIGHTER_DEPOSIT_ADDRESS = getAddress(
  '0x3b4d794a66304f130a4db8f2551b0070dfcf5ca7',
) as Address

export const LIGHTER_SLOW_DEPOSIT_TIME_ESTIMATE_SECONDS = 60 * 2 // 2 minutes
export const LIGHTER_DELAYED_AUTO_TRIGGER_CUTOFF = 60 * 5 // 5 minutes, how long to wait before auto triggering a delayed deposit message.

// ---------------------------------------------------------------------------
// L1 on-chain withdrawal fallback
//
// Lighter rate-limits L2 (Secure) withdrawals — codes 23004 / 23005, e.g.
// "L2 withdrawal rate limit exceeded. Only 3 withdrawals allowed per 2 hours".
// When that happens, the user can withdraw on-chain instead by calling
// `withdraw` on the SAME contract used for deposits (`LIGHTER_DEPOSIT_ADDRESS`).
// Verified against two mainnet txs (USDC perps `amount=1e6`, LIT spot `1e8`).
// ---------------------------------------------------------------------------

/**
 * Lighter asset index for USDC: the fallback index for a `USDC` selection —
 * the balance is exposed via `account.total_asset_value`, so USDC may be
 * absent from the per-asset list entirely.
 */
const LIGHTER_USDC_ASSET_INDEX = 3

/**
 * zkLighter L1 `withdraw` ABI — selector `0xd20191bd`, non-payable, on the same
 * contract as deposits. `amount` is in Lighter base units (see
 * {@link lighterWithdrawBaseUnits}).
 */
export const LIGHTER_WITHDRAW_ABI = [
  {
    type: 'function',
    name: 'withdraw',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'accountIndex', type: 'uint48' },
      { name: 'assetIndex', type: 'uint16' },
      { name: 'routeType', type: 'uint8' },
      { name: 'amount', type: 'uint64' },
    ],
    outputs: [],
  },
] as const

/** Strips fractional digits beyond `decimals` (string-based, no float math). */
export function truncateToDecimals(amount: string, decimals: number): string {
  const [whole = '0', frac = ''] = amount.split('.')
  if (frac.length <= decimals) {
    return amount
  }
  const truncated = frac.slice(0, decimals)
  return truncated ? `${whole}.${truncated}` : whole
}

/**
 * Lighter L2 precision for an asset, from the asset table. Not derivable from
 * the L1 token: Lighter assigns it per asset (e.g. tokenized stocks are 18 on
 * L1 but 6 on L2). Unknown indices fall back to the 18-decimal convention (8).
 */
function lighterL2Decimals(assetIndex: number): number {
  return LIGHTER_SECURE_ASSETS_BY_INDEX[assetIndex]?.l2Decimals ?? 8
}

/**
 * Converts a token-unit amount to Lighter L1 `withdraw` / L2 transfer base
 * units using {@link lighterL2Decimals} — NOT the mainnet ERC-20 decimals.
 */
export function lighterWithdrawBaseUnits(
  assetIndex: number,
  amountTokenUnits: string,
): bigint {
  const decimals = lighterL2Decimals(assetIndex)
  return parseUnits(truncateToDecimals(amountTokenUnits, decimals), decimals)
}

/**
 * Builds the on-chain `withdraw` transaction for the L1 fallback. `value` is 0
 * (no protocol fee — the user pays gas only).
 */
export function buildLighterL1WithdrawTransaction(params: {
  accountIndex: number
  assetIndex: number
  routeType: number
  amountBaseUnit: bigint
}): { to: Address; data: Hex; value: bigint } {
  const { accountIndex, assetIndex, routeType, amountBaseUnit } = params
  const data = encodeFunctionData({
    abi: LIGHTER_WITHDRAW_ABI,
    functionName: 'withdraw',
    args: [accountIndex, assetIndex, routeType, amountBaseUnit],
  })
  return { to: LIGHTER_DEPOSIT_ADDRESS, data, value: 0n }
}

const LIGHTER_EXPLORER_BASE = 'https://app.lighter.xyz/explorer/logs'
const ETHERSCAN_TX_BASE = 'https://etherscan.io/tx'

/**
 * Explorer link for a Lighter Secure withdrawal tx. The L1 on-chain fallback
 * produces a canonical 32-byte mainnet tx hash → Etherscan; L2 Secure hashes
 * are Lighter's own (non-EVM) format → Lighter explorer. Detecting the EVM hash
 * keeps L1-vs-L2 routing self-contained (no extra state threaded through the
 * modal step machine).
 */
export function resolveLighterWithdrawalExplorerUrl(txHash: string): string {
  const isMainnetTxHash = /^0x[0-9a-fA-F]{64}$/.test(txHash)
  // Encode the hash so an unexpected L2 format (e.g. containing `/` or `?`)
  // can't produce a malformed URL.
  const encoded = encodeURIComponent(txHash)
  return isMainnetTxHash
    ? `${ETHERSCAN_TX_BASE}/${encoded}`
    : `${LIGHTER_EXPLORER_BASE}/${encoded}`
}

/**
 * True when an error is Lighter's **L2 (Secure) withdrawal** rate limit —
 * codes 23004 / 23005, e.g. "Too Many L2 Withdrawal Requests!: L2 withdrawal
 * rate limit exceeded. Only 3 withdrawals allowed per 2 hours". Used to offer
 * the on-chain L1 fallback (Secure withdrawals only).
 *
 * The L2 SDK usually surfaces this as a plain string (the numeric code isn't
 * always preserved), so we match message content — plus the structured `code`
 * field when the error carries one.
 */
export function isLighterWithdrawalRateLimitError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: unknown }).code
    if (code === 23004 || code === 23005) {
      return true
    }
  }
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : ''
  const message = raw.toLowerCase()
  return (
    message.includes('23004') ||
    message.includes('23005') ||
    message.includes('rate limit') ||
    message.includes('ratelimit')
  )
}

// ---------------------------------------------------------------------------
// Secure withdrawal: L2 route types, asset identity and per-asset limits
// ---------------------------------------------------------------------------

/** Lighter L2 route type: 0 = perps (USDC collateral), 1 = spot. */
export type LighterRouteType = 0 | 1

/** Lighter L2 route types (`RouteType` in the withdraw/transfer tx payload). */
export const LIGHTER_ROUTE_TYPE = {
  PERPS: 0,
  SPOT: 1,
} as const satisfies Record<string, LighterRouteType>

/**
 * USDC exists twice on a Lighter account: as perps collateral
 * (`total_asset_value`, withdrawn via route 0) and as a spot asset balance
 * (asset entry 3, withdrawn via route 1). The Secure dropdown keys selection
 * by `symbol`, so the spot variant gets a pseudo-symbol to stay selectable
 * alongside the plain `USDC` (perps) entry.
 */
export const LIGHTER_USDC_SPOT_SYMBOL = 'USDC (Spot)'

/**
 * Strips the ` (Spot)` / ` (Perps)` variant suffix off a Lighter source-token
 * selection, returning the real token symbol (e.g. `USDC (Spot)` → `USDC`).
 * Symbol-keyed lookups (icons, prices, asset indices, min amounts) operate on
 * the base symbol; only selection identity and route resolution keep the suffix.
 */
export function getLighterBaseSymbol(symbol: string): string {
  // Single literal space, not `\s*` — an unbounded quantifier next to `(` makes
  // this quadratic on space-heavy input (CodeQL js/polynomial-redos), and the
  // suffix we mint is always exactly `" (Spot)"`.
  return symbol.replace(/ ?\((?:spot|perps)\)$/i, '')
}

/**
 * Resolves the Lighter L2 route type for a Secure source-token selection.
 * Plain `USDC` withdraws perps collateral (route 0); everything else —
 * including the `USDC (Spot)` pseudo-symbol — is a spot asset (route 1).
 */
export function resolveLighterSecureRouteType(
  selectedSourceToken: string,
): LighterRouteType {
  return selectedSourceToken.toUpperCase() === 'USDC'
    ? LIGHTER_ROUTE_TYPE.PERPS
    : LIGHTER_ROUTE_TYPE.SPOT
}

/**
 * Resolves the Lighter `asset_id` for a selected source token symbol. Returns
 * `undefined` when the token isn't in the fetched assets list (e.g. timing
 * race before balances load, or an invalid selection) — callers should treat
 * that as "not ready to submit" rather than falling back to `0`, which would
 * silently target the wrong Lighter asset.
 */
export function resolveLighterAssetIndex(
  lighterAssets: { symbol: string; asset_id: number }[],
  selectedSourceToken: string,
): number | undefined {
  // Perps and spot USDC share asset index 3 — only the route type differs.
  const selectedSymbol = getLighterBaseSymbol(selectedSourceToken).toUpperCase()
  const lookupAssetId = lighterAssets.find(
    (asset) => asset.symbol.toUpperCase() === selectedSymbol,
  )?.asset_id
  return (
    lookupAssetId ??
    (selectedSymbol === 'USDC' ? LIGHTER_USDC_ASSET_INDEX : undefined)
  )
}

/**
 * Lighter Secure withdrawal assets, keyed by Lighter L2 `asset_index`.
 *
 * `symbol`, the mainnet ERC-20's `decimals`, and Lighter's L2 `l2Decimals`
 * (the `decimals` field of Lighter's `/api/v1/assetDetails`) live here — the mainnet
 * ERC-20 destination address is provided per-call via
 * `WithdrawalParam.targetAssetAddress` (resolved by the modal via
 * `useAssetSymbolPrice`). Indices match Lighter's
 * `packages/fun-relay/src/dynamicRoutes/lighter.ts:AssetIndex` enum and the
 * spot-asset list in `apps/with-next/consts/lighterAssets.ts`. Adding a new
 * Lighter asset requires extending this table.
 */
export const LIGHTER_SECURE_ASSETS_BY_INDEX: Record<
  number,
  { symbol: string; decimals: number; l2Decimals: number }
> = {
  1: { symbol: 'ETH', decimals: 18, l2Decimals: 8 },
  2: { symbol: 'LIT', decimals: 18, l2Decimals: 8 },
  3: { symbol: 'USDC', decimals: 6, l2Decimals: 6 },
  5: { symbol: 'LINK', decimals: 18, l2Decimals: 8 },
  6: { symbol: 'UNI', decimals: 18, l2Decimals: 8 },
  7: { symbol: 'AAVE', decimals: 18, l2Decimals: 8 },
  8: { symbol: 'SKY', decimals: 18, l2Decimals: 8 },
  9: { symbol: 'LDO', decimals: 18, l2Decimals: 8 },
  10: { symbol: 'AZTEC', decimals: 18, l2Decimals: 8 },
  11: { symbol: 'XAUT', decimals: 6, l2Decimals: 6 },
}

/**
 * Mainnet ERC-20 address per Lighter Secure source token symbol. Secure bridges
 * 1:1, so the L1 destination matches the L2 source token. Native ETH uses the
 * standard sentinel; the rest are verified mainnet addresses.
 */
const LIGHTER_SECURE_MAINNET_ADDRESS: Record<string, Address> = {
  ETH: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
  LIT: '0x232CE3bd40fCd6f80f3d55A522d03f25Df784Ee2',
  USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  LINK: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
  UNI: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984',
  AAVE: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9',
  SKY: '0x56072C95FAA701256059aa122697B133aDEd9279',
  LDO: '0x5A98FcBEA516Cf06857215779Fd812CA3beF1B32',
  AZTEC: '0xA27EC0006e59f245217Ff08CD52A7E8b169E62D2',
  XAUT: '0x68749665FF8D2d112Fa859AA293F07A622782F38',
}

/**
 * Mainnet ERC-20 address for a Lighter Secure source token symbol, or undefined
 * if the symbol isn't a known Secure asset.
 */
export function getLighterSecureMainnetAddress(
  symbol: string,
): Address | undefined {
  return LIGHTER_SECURE_MAINNET_ADDRESS[
    getLighterBaseSymbol(symbol).toUpperCase()
  ]
}

/**
 * Mainnet ERC-20 decimals for a Lighter Secure source token symbol, from
 * `LIGHTER_SECURE_ASSETS_BY_INDEX`. Falls back to 18 for unknown symbols.
 */
export function getLighterSecureMainnetDecimals(symbol: string): number {
  const base = getLighterBaseSymbol(symbol).toUpperCase()
  const match = Object.values(LIGHTER_SECURE_ASSETS_BY_INDEX).find(
    (asset) => asset.symbol === base,
  )
  return match?.decimals ?? 18
}

/**
 * Default minimum Secure-withdrawal amounts (token units) for known Lighter
 * assets. Used as a fallback when `LighterWithdrawalConfig.getSecureMinWithdrawalAmount`
 * is omitted or returns `undefined` for a symbol.
 */
export const LIGHTER_DEFAULT_SECURE_MIN_WITHDRAWAL_AMOUNTS: Record<
  string,
  number
> = {
  USDC: 1,
  ETH: 0.001,
  LIT: 1,
  LINK: 0.1,
  UNI: 0.2,
  AAVE: 0.01,
  SKY: 20,
  LDO: 2,
  AZTEC: 100,
  XAUT: 0.001,
}

/**
 * Upper bound on a single Lighter Fast withdrawal, in USD. Amounts above this
 * block the CTA and surface the "submit a Secure withdrawal" footer. Fast is
 * USDC Perps only (price = 1), so this is also the token-unit cap.
 */
export const LIGHTER_FAST_MAX_WITHDRAWAL_USD = 3_000_000

// Fast withdrawal: the unstaked fee.
// - Flat deduction off the amount before quoting, waived at/above the threshold.
// - The pool is LIT-denominated; its index is hardcoded because it is currently
//   the only public pool a withdrawing user holds a share in.

export const LIGHTER_STAKING_POOL_INDEX = 281474976624800
export const LIGHTER_UNSTAKED_FEE_TOKENS = 3
export const LIGHTER_STAKING_THRESHOLD_LIT = 100

export const LIGHTER_DYNAMIC_ROUTING_ID = {
  ETH_SPOT: 'LIGHTER_ETH_SPOT',
  USDC_PERPS: 'LIGHTER_USDC_PERPS',
  ETH_SPOT_NEW_USER: 'LIGHTER_ETH_SPOT_NEW_USER',
  USDC_PERPS_NEW_USER: 'LIGHTER_USDC_PERPS_NEW_USER',
  LINK_SPOT_NEW_USER: 'LIGHTER_LINK_SPOT_NEW_USER',
  UNI_SPOT_NEW_USER: 'LIGHTER_UNI_SPOT_NEW_USER',
  AAVE_SPOT_NEW_USER: 'LIGHTER_AAVE_SPOT_NEW_USER',
  SKY_SPOT_NEW_USER: 'LIGHTER_SKY_SPOT_NEW_USER',
  LDO_SPOT_NEW_USER: 'LIGHTER_LDO_SPOT_NEW_USER',
  LIT_SPOT_NEW_USER: 'LIGHTER_LIT_SPOT_NEW_USER',
}
